import { Checkbox, Icon, Theme } from '@infotech-soe/erp-ui-kit';
import { TDatasetQueryParams } from '@infotech-soe/merps/types/Queries';
import { TDatabaseId, TSortQueryDirEnum } from '@infotech-soe/metaschema';
import React, {
  CSSProperties,
  DragEvent,
  memo,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import Draggable, { ControlPosition } from 'react-draggable';
import { useTheme } from 'react-jss';

import { LOCALES } from '../../../../constants/intl';
import { isDroppable } from '../../../../helpers';
import {
  DatasetColumnWidthConfig,
  DatasetConfig,
} from '../../../../models/user-config/DisplayModeConfig';
import { DatasetGridColumn } from '../../../../services/dataset/view-model/DatasetGridColumn';
import { DatasetGridRow } from '../../../../services/dataset/view-model/DatasetGridRow';
import { DEFAULT_COLUMN_WIDTH, FEATURES, MIN_COLUMN_WIDTH } from '../constants';
import { DatasetGridContainerProps } from '../DatasetGridContainer';
import { DEFAULT_FONT_SIZE } from '../styles';

const DRAGGABLE_EVENT_NAME = 'table-draggable-event';

export type SortChangeHandler = (field: string, multiSort?: boolean) => void;

interface HeaderCellProps {
  columnIndex: number;
  editableFields: DatasetGridContainerProps['editableFields'];
  style: CSSProperties;
  rows: DatasetGridRow[];
  orderedColumns: DatasetGridColumn[];
  selectedIds: TDatabaseId[];
  config: DatasetConfig;
  locale: LOCALES;
  conditions: TDatasetQueryParams;
  features: FEATURES[];
  onMoveColumn: (order: string[]) => void;
  onResizeColumn: (config: DatasetColumnWidthConfig) => void;
  onToggleAll: () => void;
  onSetActiveCell: (index: number) => void;
  onInverseSelection: () => void;
  onGetAutoWidth: (index: number) => number;
  onToggleShowCells: (showCells: boolean) => void;
  onSortChange: SortChangeHandler;
  onHeaderDragStart: (index: number) => void;
  searchAnchorRef?: React.Ref<HTMLDivElement>;
}

interface IndexedSortIconProps {
  dir: TSortQueryDirEnum;
  index?: number | boolean;
}

const IndexedSortIcon = ({
  dir,
  index,
}: IndexedSortIconProps): React.JSX.Element => (
  <div className="grid__sort-info">
    <Icon
      name="sort-arrow"
      className={`grid__sort-icon ${
        dir === TSortQueryDirEnum.DESC ? 'grid__sort-icon--desc' : ''
      }`}
    />
    {index && <span className="grid__sort-index">{index}</span>}
  </div>
);

const GridHeaderCell: React.FC<HeaderCellProps> = ({
  columnIndex,
  editableFields,
  style,
  rows,
  orderedColumns,
  selectedIds,
  config,
  locale,
  features,
  conditions: { fieldsConditions },
  onMoveColumn,
  onResizeColumn,
  onToggleAll,
  onInverseSelection,
  onToggleShowCells,
  onSortChange,
  onGetAutoWidth,
  onSetActiveCell,
  onHeaderDragStart,
  searchAnchorRef,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [draggablePosition, setDraggablePosition] = useState<
    ControlPosition | undefined
  >();

  const theme = useTheme<Theme>();

  const { columnsWidth, sort } = config;
  const selectableColIndex = columnIndex - 1;

  const allowDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isDroppable(event, DRAGGABLE_EVENT_NAME)) {
      event.currentTarget.classList.add('grid__header-cell--dragover');
    }
  }, []);

  const onDragStart = useCallback(
    (event: DragEvent) => {
      if (!features.includes(FEATURES.MOVE_COLUMN)) {
        event.preventDefault();
        return;
      }

      onHeaderDragStart(selectableColIndex);
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer?.setData(
        `${DRAGGABLE_EVENT_NAME}/data-columnindex`,
        (event.currentTarget as HTMLElement)?.dataset.columnindex as string
      );
    },
    [features, onHeaderDragStart, selectableColIndex]
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!isDroppable(event, DRAGGABLE_EVENT_NAME)) {
        return;
      }

      const prevIndex = Number(
        event.dataTransfer?.getData(`${DRAGGABLE_EVENT_NAME}/data-columnindex`)
      );
      const currIndex = Number(event.currentTarget?.dataset.columnindex);

      const draggedField = orderedColumns[prevIndex].field;
      const targetField = orderedColumns[currIndex].field;

      const targetPositionInFullOrder =
        config.columnsOrder.indexOf(targetField);

      const order = [...config.columnsOrder].filter(
        currentField => currentField !== draggedField
      );
      order.splice(targetPositionInFullOrder, 0, draggedField);
      onMoveColumn(order);

      onToggleShowCells(false);
      document
        .querySelector('.grid__header-cell--dragover')
        ?.classList.remove('grid__header-cell--dragover');
    },
    [orderedColumns, config.columnsOrder, onMoveColumn, onToggleShowCells]
  );

  const onDragLeave = useCallback((event: SyntheticEvent) => {
    event.currentTarget.classList.remove('grid__header-cell--dragover');
  }, []);

  const handleCheckboxClick = (e: MouseEvent<HTMLDivElement>) => {
    e.shiftKey ? onToggleAll() : onInverseSelection();
  };

  if (columnIndex === 0) {
    const checkboxContainerStyle =
      features.includes(FEATURES.CHECKBOX) && features.includes(FEATURES.LABEL)
        ? 'grid__aside'
        : 'grid__header-checkbox';

    return features.includes(FEATURES.CHECKBOX) ? (
      <div className={checkboxContainerStyle} style={style}>
        <Checkbox
          allowNull
          name="grid__header-checkbox"
          size={theme?.sizes.gridCheckbox.size}
          value={
            selectedIds.length > 0 && rows.length !== selectedIds.length
              ? null
              : rows.length > 0 && rows.length === selectedIds.length
          }
          onClick={handleCheckboxClick}
        />
      </div>
    ) : null;
  } else {
    const { rules } = config.preset[config.currentPreset];
    const { field, title, notResizable } = orderedColumns[selectableColIndex];
    const editableConfig = editableFields?.[field];
    const renamedTitle = rules[field].title?.[locale];
    const colWidth = columnsWidth[field] || DEFAULT_COLUMN_WIDTH;
    const minRangeX = -colWidth + MIN_COLUMN_WIDTH;
    const sortFiltered = sort.filter(({ dir }) => dir !== TSortQueryDirEnum.NO);
    const sortIndex = sortFiltered.findIndex(col => col.field === field);
    const callSort = sort[sort.findIndex(col => col.field === field)];

    const iconWithOrderIndex =
      callSort && callSort.dir !== TSortQueryDirEnum.NO ? (
        <IndexedSortIcon
          dir={callSort.dir}
          index={sortFiltered.length > 1 && sortIndex + 1}
        />
      ) : null;

    return (
      <div
        style={{
          ...style,
          zIndex: orderedColumns.length - columnIndex,
        }}
        className="grid__header-cell"
        ref={searchAnchorRef}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div
          onDrop={onDrop}
          onDragOver={allowDrop}
          data-columnindex={selectableColIndex}
          onClick={
            !callSort?.default
              ? e => {
                  e.preventDefault();
                  onSortChange(field, e.ctrlKey || e.metaKey);
                }
              : undefined
          }
          onDragLeave={onDragLeave}
          onContextMenu={() => {
            onSetActiveCell(selectableColIndex);
          }}
        >
          <div
            draggable
            data-columnindex={selectableColIndex}
            onDragStart={onDragStart}
            className={`grid__header-dnd-cell ${
              features.includes(FEATURES.SORTING) && !callSort?.default
                ? 'grid__header-dnd-cell'
                : 'grid__header-dnd-cell--cursor'
            }`}
          >
            <div
              className={`grid__header-cell-title${
                editableConfig ? ' grid__header-cell-editable' : ''
              }`}
            >
              {renamedTitle || title}
            </div>
            <div className="grid__header-icons">
              {fieldsConditions[field] && <Icon name="filter-column" />}
              {iconWithOrderIndex}
            </div>
          </div>
        </div>
        <Draggable
          nodeRef={buttonRef}
          position={draggablePosition}
          axis="x"
          bounds={{ left: minRangeX }}
          disabled={notResizable}
          onStop={(_e, { x, node }) => {
            if (x !== 0) {
              setDraggablePosition({ x: 0, y: 0 });
              onResizeColumn({
                field,
                width: x + (node.parentNode as HTMLElement)?.offsetWidth,
              });
            }
          }}
        >
          <button
            className="grid__resize-button"
            ref={buttonRef}
            onDoubleClick={() => {
              onResizeColumn({
                field,
                width:
                  onGetAutoWidth(selectableColIndex) +
                  (iconWithOrderIndex ? DEFAULT_FONT_SIZE : 0),
              });
            }}
            disabled={notResizable}
          />
        </Draggable>
      </div>
    );
  }
};

export default memo(GridHeaderCell);
